import * as React from 'react';
import Select from 'react-select';
import {
    AudienceRule as IAudienceRule,
    AudienceRuleOption,
    MULTIPLE_ANSWER_AUDIENCE_RULE_MODE,
    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES
} from '@nexxt/common/types';
import { NA_OPTION_VALUE } from '@nexxt/common/constants';
import AudienceRuleHeader from '../AudienceRuleHeader';
import * as _ from 'lodash';
interface MultipleAudienceRuleProps {
    audienceRuleOptions: AudienceRuleOption[];
    audienceRule: IAudienceRule;
    halfWidth: boolean;
    isMenuOpen: boolean;
    isDisabled: boolean;
    health?: any;
    setIsMenuOpen: () => void;
    onEdit: (editingQuestion) => void;
    onDelete: () => void;
    onChangeMultipleAnswerMode: (
        ruleMode: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
    ) => void;
    onChangeMultipleAnswerRadioButton: (
        index: number,
        ruleType: MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES,
        ruleMode?: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
    ) => void;
    disableByFieldName: (keyName?: string) => boolean;
}
const MAPPING_MUST_SELECT_MODE = [
    {
        label: 'Any',
        value: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ANY
    },
    {
        label: 'All',
        value: MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ALL
    }
];
const MultipleAudienceRule = ({
    audienceRuleOptions,
    audienceRule,
    halfWidth,
    isDisabled,
    isMenuOpen,
    health,
    setIsMenuOpen,
    onEdit,
    onDelete,
    onChangeMultipleAnswerMode,
    onChangeMultipleAnswerRadioButton,
    disableByFieldName
}: MultipleAudienceRuleProps): JSX.Element => {
    const selectDropdownStyles = {
        control: (base) => ({
            ...base,
            background: '#F2F2F2',
            height: 28,
            minHeight: 28,
            borderWidth: 0
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: 4
        })
    };
    const mustSelectModeOption = audienceRule.audienceRuleOptions?.find(
        (opt) => opt.mode?.ruleMode
    );
    let mustSelectModeValue;
    if (mustSelectModeOption) {
        mustSelectModeValue = MAPPING_MUST_SELECT_MODE.find(
            (mode) => mode.value === mustSelectModeOption.mode.ruleMode
        );
    } else {
        mustSelectModeValue = MAPPING_MUST_SELECT_MODE[0];
    }

    const renderOptions = (options) => {
        return options.map((option) => {
            const radioGroupName = `${audienceRule?.selectedQuestion?.value}-${option.value}`;
            const selectedRuleType =
                option?.mode?.ruleType ||
                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.DISQUALIFY;
            const index = audienceRule.audienceRuleOptions.findIndex(
                (audienceOption) => _.isEqual(audienceOption, option)
            );
            return (
                <tr
                    className="option-row"
                    key={`${audienceRule.selectedQuestion.label}-${option.id}-${index}`}
                >
                    <td>{option.label}</td>
                    <td align="center">
                        <input
                            type="radio"
                            name={radioGroupName}
                            readOnly
                            checked={
                                selectedRuleType ===
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
                            }
                            disabled={
                                isDisabled ||
                                (option.value === NA_OPTION_VALUE &&
                                    mustSelectModeOption?.mode?.ruleMode ===
                                        MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ALL)
                            }
                            onChange={() =>
                                onChangeMultipleAnswerRadioButton(
                                    index,
                                    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT,
                                    mustSelectModeValue.value
                                )
                            }
                        />
                    </td>
                    <td align="center">
                        <input
                            type="radio"
                            name={radioGroupName}
                            readOnly
                            disabled={isDisabled}
                            checked={
                                selectedRuleType ===
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.DISQUALIFY
                            }
                            onChange={() =>
                                onChangeMultipleAnswerRadioButton(
                                    index,
                                    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.DISQUALIFY
                                )
                            }
                        />
                    </td>
                    <td align="center">
                        <input
                            type="radio"
                            name={radioGroupName}
                            readOnly
                            checked={
                                selectedRuleType ===
                                MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
                            }
                            disabled={
                                isDisabled ||
                                (option.value === NA_OPTION_VALUE &&
                                    mustSelectModeOption?.mode?.ruleMode ===
                                        MULTIPLE_ANSWER_AUDIENCE_RULE_MODE.ALL)
                            }
                            onChange={() =>
                                onChangeMultipleAnswerRadioButton(
                                    index,
                                    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MAY_SELECT
                                )
                            }
                        />
                    </td>
                </tr>
            );
        });
    };
    return (
        <>
            <AudienceRuleHeader
                audienceRule={audienceRule}
                isDisabled={isDisabled}
                quotaEnabled={false}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                onEdit={onEdit}
                onDelete={onDelete}
                isBuiltIn={false}
                isHalfWidth={halfWidth}
                disableByFieldName={disableByFieldName}
                health={health}
            />
            {halfWidth ? (
                <table>
                    <colgroup>
                        <col span={1} className="column-4" />
                        <col span={1} className="column-4" />
                        <col span={1} className="column-4" />
                        <col span={1} className="column-4" />
                    </colgroup>
                    <tbody>
                        <tr className="content-header">
                            <td align="center">Answer</td>
                            <td
                                align="center"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <span
                                    style={{
                                        marginRight: '5px'
                                    }}
                                >
                                    Must Select
                                </span>
                                <div className="dropdown-container--m-select">
                                    <Select
                                        options={MAPPING_MUST_SELECT_MODE}
                                        onChange={(selectedOption) =>
                                            onChangeMultipleAnswerMode(
                                                selectedOption.value as MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
                                            )
                                        }
                                        value={mustSelectModeValue}
                                        isSearchable={false}
                                        styles={selectDropdownStyles}
                                        isDisabled={isDisabled}
                                        components={{
                                            IndicatorSeparator: () => null
                                        }}
                                    />
                                </div>
                            </td>
                            <td align="center">Disqualify</td>
                            <td align="center">May Select</td>
                        </tr>
                        {renderOptions(audienceRuleOptions)}
                    </tbody>
                </table>
            ) : (
                <div className="wide-screener-container">
                    {audienceRuleOptions.map((options, ind) => (
                        <table
                            style={{
                                marginRight: ind === 0 && '50px'
                            }}
                            key={
                                ind === 0
                                    ? 'leftColumn-options'
                                    : 'rightColumn-options'
                            }
                        >
                            <colgroup>
                                <col span={1} className="column-4" />
                                <col span={1} className="column-4" />
                                <col span={1} className="column-4" />
                                <col span={1} className="column-4" />
                            </colgroup>
                            <tbody>
                                <tr className="content-header">
                                    <td align={ind === 0 ? 'left' : 'justify'}>
                                        Answer
                                    </td>
                                    <td
                                        align="center"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <span
                                            style={{
                                                marginRight: '5px'
                                            }}
                                        >
                                            Must Select
                                        </span>
                                        <div className="dropdown-container--m-select">
                                            <Select
                                                options={
                                                    MAPPING_MUST_SELECT_MODE
                                                }
                                                onChange={(selectedOption) =>
                                                    onChangeMultipleAnswerMode(
                                                        selectedOption.value as MULTIPLE_ANSWER_AUDIENCE_RULE_MODE
                                                    )
                                                }
                                                value={mustSelectModeValue}
                                                isSearchable={false}
                                                styles={selectDropdownStyles}
                                                isDisabled={isDisabled}
                                                components={{
                                                    IndicatorSeparator: () =>
                                                        null
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td align="center">Disqualify</td>
                                    <td align="center">May Select</td>
                                </tr>
                                {renderOptions(options)}
                            </tbody>
                        </table>
                    ))}
                </div>
            )}
        </>
    );
};

export default MultipleAudienceRule;
